import { type ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';

import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import { useTheme } from '@mui/material/styles';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import isEqual from 'lodash/isEqual';

import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { InfoAlert } from '../InfoAlertField/InfoAlertField';
import { InputGroupCheckboxCardUiOptions } from './schema';

export { InputGroupCheckboxCardSchema } from './schema';

// Field status type for the input
type FieldStatus = 'default' | 'success' | 'error';

// Property definition from the JSON schema
interface PropertyEntry {
  title?: string;
  description?: string;
  type?: string;
  format?: string;
  pattern?: string;
  errorMessage?: string;
  const?: string;
  default?: string;
}

/**
 * InputGroupCheckboxCard Field Component
 * Displays a card with an info alert, a checkbox to reveal a secret field,
 * and always-visible fields (e.g. expiration date).
 */
export const InputGroupCheckboxCard = (
  props: FieldExtensionComponentProps<object, InputGroupCheckboxCardUiOptions>,
) => {
  const { onChange, formData, uiSchema, schema, disabled, readonly, required } =
    props;

  const {
    title,
    description,
    properties = {},
  } = schema as {
    title?: string;
    description?: string;
    properties?: Record<string, PropertyEntry>;
  };

  const theme = useTheme();
  const backgroundColor = theme.palette.mode === 'dark' ? '#0D0D0D' : '#F4F4F4';

  // Extract ui:options
  const uiOptions =
    (uiSchema?.['ui:options'] as InputGroupCheckboxCardUiOptions | undefined) ??
    {};
  const infoText = uiOptions.infoText;
  const checkboxLabel = uiOptions.checkboxLabel ?? 'Replace value';
  // The property key for the field gated by the checkbox
  const secretField = uiOptions.secretField ?? '';
  const isSecret = uiOptions.isSecret ?? false;

  // Always-visible property keys: exclude the checkbox-gated field and const fields
  const visibleKeys = useMemo(
    () =>
      Object.keys(properties).filter(
        key =>
          key !== secretField &&
          (properties[key] as PropertyEntry)?.const === undefined,
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(properties), secretField],
  );

  // Local state for all property values
  const [propertyValues, setPropertyValues] = useState<Record<string, string>>(
    () => (formData as Record<string, string>) ?? {},
  );

  // Per-field validation status and helper text
  const [fieldStatuses, setFieldStatuses] = useState<
    Record<string, FieldStatus>
  >({});
  const [fieldHelpers, setFieldHelpers] = useState<Record<string, string>>({});

  // State for checkbox: whether to show the secret field
  const [isChecked, setIsChecked] = useState(false);

  // State for password visibility toggle
  const [showSecret, setShowSecret] = useState(false);

  // Ref for debounced onChange to parent
  const onChangeTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const lastEmittedRef = useRef<any>(formData ?? null);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (onChangeTimerRef.current) clearTimeout(onChangeTimerRef.current);
    };
  }, []);

  // Sync local state to parent via onChange when values change
  useEffect(() => {
    if (!isEqual(lastEmittedRef.current, propertyValues)) {
      if (onChangeTimerRef.current) clearTimeout(onChangeTimerRef.current);
      onChangeTimerRef.current = setTimeout(() => {
        lastEmittedRef.current = propertyValues;
        onChangeRef.current?.(propertyValues);
      }, 0);
    }
  }, [propertyValues]);

  /**
   * Sets field status and helper text for a given key
   * @param key The property key
   * @param status The field status
   * @param text The helper text
   */
  function setFieldState(key: string, status: FieldStatus, text: string) {
    setFieldStatuses(prev => ({ ...prev, [key]: status }));
    setFieldHelpers(prev => ({ ...prev, [key]: text }));
  }

  /**
   * Validates a property value against required and pattern constraints
   * @param key The property key
   * @param value The current value
   */
  function validateProperty(key: string, value: string) {
    const property = properties[key] as PropertyEntry;
    const defaultHelper = property?.description ?? '';

    if (required && value.trim().length === 0) {
      setFieldState(
        key,
        'error',
        property?.errorMessage ?? 'This is a required field.',
      );
      return;
    }

    if (
      property?.pattern &&
      value.trim().length > 0 &&
      !new RegExp(property.pattern).test(value)
    ) {
      setFieldState(key, 'error', property?.errorMessage ?? 'Invalid format.');
      return;
    }

    setFieldState(key, 'default', defaultHelper);
  }

  // Handle value change for always-visible fields
  const handlePropertyChange =
    (key: string) => (event: ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setPropertyValues(prev => ({ ...prev, [key]: value }));
      validateProperty(key, value);
    };

  // Handle checkbox toggle for the secret field
  const handleCheckboxChange = (event: ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    setIsChecked(checked);

    if (!checked) {
      // Clear the secret value when unchecking
      setPropertyValues(prev => {
        const next = { ...prev };
        delete next[secretField];
        return next;
      });
      setFieldState(secretField, 'default', '');
    }
  };

  // Handle secret field value change
  const handleSecretChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setPropertyValues(prev => ({ ...prev, [secretField]: value }));
    validateProperty(secretField, value);
  };

  // Resolve secret property metadata
  const secretProperty = properties[secretField] as PropertyEntry | undefined;
  const secretPlaceholder =
    (uiSchema as any)?.[secretField]?.['ui:placeholder'] ?? '';

  return (
    <Box sx={{ backgroundColor, padding: '1rem', borderRadius: '0.25rem' }}>
      {title && (
        <Box
          component="h2"
          sx={{ marginBottom: '0.5rem', marginTop: 0, fontSize: '1.125rem' }}
        >
          {title}
        </Box>
      )}

      {description && <Box sx={{ marginBottom: '0.5rem' }}>{description}</Box>}

      {(title || description) && (
        <Divider sx={{ marginBottom: '1rem', marginTop: '0.5rem' }} />
      )}

      {/* Info alert showing that a value is already configured */}
      <InfoAlert text={infoText} sx={{ mb: '1.5rem' }} />

      {/* Checkbox to toggle the secret field visibility */}
      {secretField && (
        <Box sx={{ mb: '1rem' }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={isChecked}
                onChange={handleCheckboxChange}
                disabled={disabled || readonly}
              />
            }
            label={checkboxLabel}
          />
        </Box>
      )}

      {/* Secret field (only visible when checkbox is checked) */}
      {secretField && isChecked && secretProperty && (
        <Box sx={{ mb: '1rem' }}>
          <TextField
            label={secretProperty.title ?? secretField}
            placeholder={secretPlaceholder}
            value={propertyValues[secretField] ?? ''}
            onChange={handleSecretChange}
            onBlur={() =>
              validateProperty(secretField, propertyValues[secretField] ?? '')
            }
            disabled={disabled || readonly}
            required={required as any}
            type={isSecret && !showSecret ? 'password' : 'text'}
            variant="outlined"
            fullWidth
            error={fieldStatuses[secretField] === 'error'}
            helperText={
              fieldHelpers[secretField] ?? secretProperty.description ?? ''
            }
            InputProps={{
              endAdornment: isSecret ? (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="Toggle visibility"
                    onClick={() => setShowSecret(prev => !prev)}
                    edge="end"
                    size="small"
                  >
                    {showSecret ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            }}
          />
        </Box>
      )}

      {/* Always-visible fields (e.g. expiration date) */}
      {visibleKeys.map(key => {
        const property = properties[key] as PropertyEntry;
        const placeholder = (uiSchema as any)?.[key]?.['ui:placeholder'] ?? '';
        const status = fieldStatuses[key] ?? 'default';

        return (
          <Box key={key} sx={{ mb: '1rem' }}>
            <TextField
              label={property?.title ?? key}
              placeholder={placeholder}
              value={propertyValues[key] ?? ''}
              onChange={handlePropertyChange(key)}
              onBlur={() => validateProperty(key, propertyValues[key] ?? '')}
              disabled={disabled || readonly}
              required={required as any}
              type={property?.format === 'email' ? 'email' : 'text'}
              variant="outlined"
              fullWidth
              error={status === 'error'}
              helperText={fieldHelpers[key] ?? property?.description ?? ''}
            />
          </Box>
        );
      })}
    </Box>
  );
};
